import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { SafeHtml } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import {
  LayoutLibModule,
  NotificationFacade,
} from '@g99-product-web-ui/layout-lib';
import { PrimeNgModule } from '@g99-product-web-ui/prime-ng';
import { SharedFeatureComponentLibModule } from '@g99-product-web-ui/shared-feature-component-lib';
import {
  IPublicBusinessConfig,
  TwoWayTextSetupStatus,
} from '@g99-product-web-ui/shared-interface-lib';
import {
  LocalStorageService,
  SanitizeService,
  SharedBusinessService,
  SubjectManageService,
  ToastService,
} from '@g99-product-web-ui/shared-service';
import {
  GOOGLE_BANNER_CONFIG,
  LOCAL_STORAGE_KEYS,
  TWILIO_BANNER_CONFIG,
  closeToastrIcon,
  errorToastrIcon,
  infoToastrIcon,
  successToastrIcon,
  warningToastrIcon,
} from '@g99-product-web-ui/shared-util-lib';
import { ProductFruitsObject, productFruits } from 'product-fruits';

import { ToastType } from './app.constant';
import { WebMcpToolsService } from './web-mcp-tools.service';

declare global {
  interface Window {
    productFruits?: ProductFruitsObject;
  }
}

@Component({
  imports: [
    CommonModule,
    RouterModule,
    LayoutLibModule,
    PrimeNgModule,
    SharedFeatureComponentLibModule,
  ],
  selector: 'g99-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  public isAuthenticated = false;

  protected closeIcon!: SafeHtml;

  protected twilioConfig = TWILIO_BANNER_CONFIG;

  protected googleConfig = GOOGLE_BANNER_CONFIG;

  protected showTwilioSetupBanner = false;
  protected showGoogleBanner = false;

  protected maintainenceBannerTitle = 'Review Manager System Update';
  protected maintainenceBannerDescription =
    'We are currently scaling up our Review Manager system. During this time, retrieving reviews from Google, Yelp, and Facebook may be delayed. Services are expected to resume by 18th November 2025 at 10:00 AM EST.';

  constructor(
    private readonly _subjectManagerService: SubjectManageService,
    private readonly _localStorageService: LocalStorageService,
    private readonly _toastService: ToastService,
    private readonly _sanitizerService: SanitizeService,
    private readonly _sharedBusinessService: SharedBusinessService,
    private readonly _notificationFacade: NotificationFacade,
    private readonly _webMcpToolsService: WebMcpToolsService
  ) {}

  ngOnInit(): void {
    this.loadPf();
    const expirationTime =
      this._localStorageService.readItem<string>('tokenExpiration');

    if (
      expirationTime &&
      this._subjectManagerService.isValidReturnUrl(window.location.pathname)
    ) {
      const expTime = parseInt(expirationTime, 10);
      this._subjectManagerService.startAutoLogoutTimer(expTime);

      if (Date.now() >= expTime) {
        this._subjectManagerService.logout(); // Token already expired
      }
    }
    // Initialize subjects from local storage on refresh
    this._subjectManagerService.initializeSubjects();
    this._refreshBusinessConfigOnBootstrap();

    // Subscribe to userSubject to show/hide the header
    this._subjectManagerService.userSubject.subscribe((user) => {
      this.isAuthenticated =
        !!user &&
        !(user?.mfaForced || user.challengeName) &&
        !user.changePasswordForced; // Set to true if user is present

      if (this.isAuthenticated) {
        this._notificationFacade.ensureRealtimeStarted();
      }

      if (this.isAuthenticated) {
        this._webMcpToolsService.registerTools();

        if (!user?.supportUser) {
          this._fetchTwilioSetupStatus();
          this._showGoogleAccountSetupBanner();
        }
      } else {
        this._webMcpToolsService.deregisterTools();
      }
    });
    this.closeIcon = this._sanitizerService.sanitizeContent(closeToastrIcon);
  }

  protected loadPf = () => {
    productFruits.init(
      'QocEttCrxOm6hFpU',
      'en',
      { username: 'test' },
      { disableLocationChangeDetection: false }
    );

    productFruits.safeExec(() => {
      // product fruits initialized
    });
  };

  /**
   * Clears the toast message with the given key.
   *
   * @param key - The unique identifier for the toast message to be cleared.
   */
  protected clearToast(key: string): void {
    // Clears the toast based on its unique key.
    this._toastService.clearToast(key);
  }

  /**
   * Returns the sanitized HTML for the toast icon based on its severity type.
   * This ensures that any SVG or HTML icons are safe to use in the template.
   *
   * @param severity - The severity of the toast (e.g., SUCCESS, ERROR, INFO, etc.).
   * @returns SafeHtml - The sanitized HTML for the icon that is safe for binding.
   */
  protected getIconForToastType(severity: ToastType): SafeHtml {
    // Fetch the raw icon based on the severity of the toast.
    const rawIcon = this._getIcon(severity);

    // Sanitize and return the raw icon HTML to ensure it is safe for use in the template.
    return this._sanitizerService.sanitizeContent(rawIcon);
  }

  protected closeTwilioBanner(): void {
    this.showTwilioSetupBanner = false;
  }

  protected closeGoogleBanner(): void {
    this.showGoogleBanner = false;
  }

  /**
   * Helper method to retrieve the raw icon for a given toast severity type.
   * This is used by `getIconForToastType` to select the appropriate icon.
   *
   * @param type - The severity type of the toast (SUCCESS, ERROR, INFO, etc.).
   * @returns string - The raw HTML or SVG string representing the icon for the toast type.
   */
  private _getIcon(type: ToastType): string {
    // Switch statement to return the corresponding raw icon for each severity type.
    switch (type) {
      case ToastType.SUCCESS:
        return successToastrIcon;
      case ToastType.ERROR:
        return errorToastrIcon;
      case ToastType.INFO:
        return infoToastrIcon;
      case ToastType.WARNING:
        return warningToastrIcon;
      default:
        return ''; // Default case if no matching severity type is found.
    }
  }

  /**
   * Refreshes business config on every bootstrap/refresh so flag changes made in
   * the admin panel (e.g. disabling Appointments) take effect on the next refresh.
   */
  private _refreshBusinessConfigOnBootstrap(): void {
    const business = this._localStorageService.readItem<IPublicBusinessConfig>(
      LOCAL_STORAGE_KEYS.BUSINESS_INFO
    );
    if (business?.id) {
      this._sharedBusinessService.refreshBusinessConfig(business.id);
    }
  }

  private _fetchTwilioSetupStatus() {
    const { registrationStatus } =
      this._sharedBusinessService.getTwoWayTextBannerStatus();
    this.showTwilioSetupBanner =
      registrationStatus === TwoWayTextSetupStatus.SETUP_PENDING;
  }

  private _showGoogleAccountSetupBanner(): void {
    // Retrieve the stored banner visibility flag from local storage.
    const showBannerStored = this._localStorageService.readItem(
      LOCAL_STORAGE_KEYS.GOOGLE_BANNER_STATUS_KEY
    );
    this.showGoogleBanner = showBannerStored === true;
    // If the flag exists and is set to 'true', display the banner and exit early.
    if (this.showGoogleBanner) {
      return;
    }

    const businessInfo: IPublicBusinessConfig =
      this._localStorageService.readItem(LOCAL_STORAGE_KEYS.BUSINESS_INFO) ||
      ({} as IPublicBusinessConfig);
    if (businessInfo && businessInfo.id) {
      // Fetch the Google setup status from the server.
      this._sharedBusinessService
        .getGoogleSetupStatus()
        .subscribe((response) => {
          // Check the response for the `showBanner` flag.
          const shouldShowBanner = response?.showBanner;

          // Update local storage and banner visibility based on the response.
          this._localStorageService.storeItem(
            LOCAL_STORAGE_KEYS.GOOGLE_BANNER_STATUS_KEY,
            shouldShowBanner ? 'true' : 'false'
          );
          this.showGoogleBanner = shouldShowBanner || false;
        });
    }
  }
}
